import { useRef } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import DownloadIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { fmtRs } from "../../utils/payrollFormat";

const PRIMARY = "#d97706";
const COMPANY_NAME = "Dockyard General Engineering Services (Pvt) Ltd";

// One line item row inside the Earnings / Deductions mini-tables.
// const LineRow = ({ label, amount, negative }) => (
//   <TableRow>
//     <TableCell sx={{ border: 0, py: 0.5, pl: 0, fontSize: 14 }}>{label}</TableCell>
//     <TableCell sx={{ border: 0, py: 0.5, pr: 0, fontSize: 14 }} align="right">
//       {negative ? `-${fmtRs(Math.abs(amount))}` : fmtRs(amount)}
//     </TableCell>
//   </TableRow>
// );

// One line item row inside the Earnings / Deductions mini-tables.
const LineRow = ({ label, amount, negative }) => (
  <TableRow>
    <TableCell
      sx={{
        border: 0,
        py: 0.5,
        pl: 0,
        fontSize: 14,
      }}
    >
      {label}
    </TableCell>

    <TableCell
      sx={{
        border: 0,
        py: 0.5,
        pr: 0,
        fontSize: 14,
      }}
      align="right"
    >
      {negative
        ? `-${fmtRs(Math.abs(amount))}`
        : fmtRs(amount)}
    </TableCell>
  </TableRow>
);

/**
 * Printable / downloadable salary slip.
 *
 * employee: { name, empNo, designation, department } — display only
 * period:   { name } — e.g. "January 2026"
 * header:   normalized payrollHeaders row (attendance + authoritative totals)
 * breakdown: { earnings, deductions, totalEarnings, totalDeductions, netSalary }
 *            from utils/salarySlip.buildSalarySlipBreakdown
 *
 * Print / "Download PDF" both go through the browser's native print
 * dialog (window.print scoped to this component via a print stylesheet),
 * so "Save as PDF" in that dialog produces the PDF — no extra libraries,
 * no server round-trip.
 */
const SalarySlip = ({ employee, period, header, breakdown, slipId = "salary-slip-print", batchMode = false }) => {
  const printRef = useRef(null);

  const handlePrint = () => window.print();

  if (!header || !breakdown) return null;

  return (
    <Box>
      {!batchMode && (
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }

            #${slipId},
            #${slipId} * {
              visibility: visible;
            }

            #${slipId} {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
            }

            .salary-slip-actions {
              display: none !important;
            }
          }
        `}</style>
      )}

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        className="salary-slip-actions"
        sx={{ mb: 2 }}
      >
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handlePrint}
          sx={{ textTransform: "none", borderColor: PRIMARY, color: PRIMARY }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ textTransform: "none", bgcolor: PRIMARY, "&:hover": { bgcolor: "#164A1A" } }}
        >
          Print
        </Button>
      </Stack>

      <Paper
        id={slipId}
        ref={printRef}
        elevation={0}
        sx={{ 
          borderRadius: 3, 
          border: "1px solid #EAEAEA", 
          p: { xs: 2, sm: 4 },
          pageBreakAfter: batchMode ? "auto" : "always",
          breakAfter: batchMode ? "auto" : "page",
          mb: { xs: 2, print: 0 },
        }}
      >
        {/* Company header */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color={PRIMARY}>
            {COMPANY_NAME}
          </Typography>
          <Typography variant="body2" color="#646464">
            Salary Slip — {period?.name || "-"}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2, borderColor: PRIMARY, borderBottomWidth: 2 }} />

        {/* Employee info */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2"><b>Employee:</b> {employee?.name || "-"} {employee?.empNo ? `(ID: ${employee.empNo})` : ""}</Typography>
            {employee?.designation && (
              <Typography variant="body2"><b>Designation:</b> {employee.designation}</Typography>
            )}
            {employee?.department && (
              <Typography variant="body2"><b>Department:</b> {employee.department}</Typography>
            )}
          </Grid>
          <Grid item xs={6} sx={{ textAlign: { sm: "right" } }}>
            <Typography variant="body2"><b>Period:</b> {period?.name || "-"}</Typography>
            {header.payDate && (
              <Typography variant="body2"><b>Pay Date:</b> {header.payDate}</Typography>
            )}
            {header.payrollNo && (
              <Typography variant="body2"><b>Payroll No:</b> {header.payrollNo}</Typography>
            )}
          </Grid>
        </Grid>

        {/* Attendance summary */}
        <Grid
          container
          spacing={1}
          sx={{ mb: 3, bgcolor: "#F5F8F5", borderRadius: 2, p: 1.5, textAlign: "center" }}
        >
          {[
            ["Working Days", header.workingDays],
            ["Present Days", header.presentDays],
            ["No-Pay Days", header.noPayDays],
            ["OT Hours", header.otHours],
            // ["Leave Days", header.leaveDays],
          ].map(([label, value]) => (
            <Grid item xs={12/4} key={label}>
              <Typography variant="caption" color="#646464" display="block">{label}</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{value ?? 0}</Typography>
            </Grid>
          ))}
        </Grid>

        {/* Earnings / Deductions */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} color={PRIMARY} sx={{ mb: 0.5 }}>
              EARNINGS
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <Table size="small">
              <TableBody>
                {breakdown.earnings.map((e, i) => (
                  <LineRow key={i} label={e.label} amount={e.amount} />
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ mt: 0.5 }} />
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, pl: 0, fontWeight: 700, fontSize: 14 }}>Total Earnings</TableCell>
                  <TableCell sx={{ border: 0, py: 0.5, pr: 0, fontWeight: 700, fontSize: 14 }} align="right">
                    {fmtRs(breakdown.totalEarnings)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" fontWeight={700} color="#B3261E" sx={{ mb: 0.5 }}>
              DEDUCTIONS
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <Table size="small">
              <TableBody>
                {breakdown.deductions.map((d, i) => (
                  <LineRow key={i} label={d.label} amount={d.amount} negative />
                ))}
              </TableBody>
            </Table>
            <Divider sx={{ mt: 0.5 }} />
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, py: 0.5, pl: 0, fontWeight: 700, fontSize: 14 }}>Total Deductions</TableCell>
                  <TableCell sx={{ border: 0, py: 0.5, pr: 0, fontWeight: 700, fontSize: 14 }} align="right">
                    -{fmtRs(breakdown.totalDeductions)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>

        {/* Net payable */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: PRIMARY,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color="#fff">
            NET PAYABLE
          </Typography>
          <Typography variant="h6" fontWeight={700} color="#fff">
            {fmtRs(breakdown.netSalary)}
          </Typography>
        </Box>

        <Typography variant="caption" color="#9A9A9A" display="block" sx={{ mt: 2, textAlign: "center" }}>
          This is a system-generated salary slip and does not require a signature.
        </Typography>
      </Paper>
    </Box>
  );
};

export default SalarySlip;
